import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { FaPlus, FaEye, FaPaperPlane, FaUndo } from 'react-icons/fa';
import { changeControlAPI } from '../services/api';
import useApprovalRights from '../hooks/useApprovalRights';
import { Modal } from '../components/Modals';
import StatusBadge from '../components/StatusBadge';
import TablePagination from '../components/TablePagination';

const FLOW = 'system_change.approval';
const CHANGE_TYPES = ['Feature', 'Bug Fix', 'Config', 'Security', 'Data/Migration'];

const EMPTY_FORM = {
  version: '', title: '', change_type: 'Feature', description: '', reason: '',
  risk_assessment: '', impact_assessment: '', test_evidence: '', rollback_plan: ''
};

const getRows = (payload) => (Array.isArray(payload?.data?.data) ? payload.data.data : []);

const ChangeControl = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [saving, setSaving] = useState(false);

  const [formModal, setFormModal] = useState(null); // { id|null, ...EMPTY_FORM }
  const [viewModal, setViewModal] = useState(null); // row
  const [decideModal, setDecideModal] = useState(null); // { record, decision, comment, password }

  const rights = useApprovalRights(FLOW);

  const load = async () => {
    setLoading(true);
    try {
      const res = await changeControlAPI.getAll();
      setRows(getRows(res));
    } catch (err) {
      console.error('Failed to load change control records:', err);
      toast.error('Failed to load change control records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const openCreate = () => setFormModal({ id: null, ...EMPTY_FORM });
  const openEdit = (row) => setFormModal({
    id: row.change_id, version: row.version || '', title: row.title || '', change_type: row.change_type || 'Feature',
    description: row.description || '', reason: row.reason || '', risk_assessment: row.risk_assessment || '',
    impact_assessment: row.impact_assessment || '', test_evidence: row.test_evidence || '', rollback_plan: row.rollback_plan || ''
  });

  const saveForm = async () => {
    if (!formModal.title.trim()) return toast.error('Title is required');
    setSaving(true);
    try {
      const payload = { ...formModal };
      delete payload.id;
      if (formModal.id) await changeControlAPI.update(formModal.id, payload);
      else await changeControlAPI.create(payload);
      toast.success('Saved');
      setFormModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const removeDraft = async (row) => {
    if (!window.confirm(`Delete draft "${row.title}"? This cannot be undone.`)) return;
    try {
      await changeControlAPI.remove(row.change_id);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete');
    }
  };

  const submitForApproval = async (row) => {
    try {
      await changeControlAPI.submit(row.change_id);
      toast.success('Submitted for QA approval');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to submit');
    }
  };

  const openDecide = (row, decision) => setDecideModal({ record: row, decision, comment: '', password: '' });
  const submitDecide = async () => {
    if (!decideModal.password) return toast.error('Password required');
    setSaving(true);
    try {
      await changeControlAPI.decide(decideModal.record.change_id, {
        decision: decideModal.decision, comment: decideModal.comment, password: decideModal.password
      });
      toast.success(decideModal.decision === 'approve' ? 'Approved and released' : decideModal.decision === 'return' ? 'Returned to draft' : 'Rejected');
      setDecideModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to record decision');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>System Change Control</h1>
          <p className="text-muted">
            Validation evidence for the app itself: every change is risk- and impact-assessed, tested, and
            QA-signed before it's marked Released. Separate from the GxP records this app produces for your
            own cleaning validation program.
          </p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><FaPlus /> New Change Record</button>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Title</th><th>Version</th><th>Type</th><th>Status</th><th>Created By</th><th>Created</th><th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-muted">Loading...</td></tr>
            ) : pagedRows.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-muted">No change records yet</td></tr>
            ) : pagedRows.map((row) => (
              <tr key={row.change_id}>
                <td>
                  <span className="text-primary cursor-pointer font-medium" onClick={() => setViewModal(row)}>{row.title}</span>
                </td>
                <td>{row.version || '—'}</td>
                <td>{row.change_type}</td>
                <td><StatusBadge status={row.status} /></td>
                <td>{row.created_by_name || '—'}</td>
                <td>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button className="btn btn-outline btn-sm" title="View" onClick={() => setViewModal(row)}><FaEye /></button>
                    {row.status === 'Draft' && (
                      <>
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(row)}>Edit</button>
                        <button className="btn btn-primary btn-sm" title="Submit for QA approval" onClick={() => submitForApproval(row)}><FaPaperPlane /> Submit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => removeDraft(row)}>Delete</button>
                      </>
                    )}
                    {row.status === 'Pending QA Approval' && rights.canFinal && (
                      <>
                        <button className="btn btn-success btn-sm" onClick={() => openDecide(row, 'approve')}>Approve &amp; Release</button>
                        <button className="btn btn-outline btn-sm" onClick={() => openDecide(row, 'return')}><FaUndo /> Return</button>
                        <button className="btn btn-danger btn-sm" onClick={() => openDecide(row, 'reject')}>Reject</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination total={rows.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} itemLabel="records" />

      {/* ── Create / Edit Modal ──────────────────────────────────────────────── */}
      {formModal && (
        <Modal
          isOpen
          onClose={() => setFormModal(null)}
          title={formModal.id ? 'Edit Change Record' : 'New Change Record'}
          size="lg"
          footer={
            <>
              <button className="btn btn-outline" onClick={() => setFormModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveForm} disabled={saving}>{saving ? 'Saving...' : 'Save Draft'}</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-control" value={formModal.title} onChange={(e) => setFormModal((p) => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="form-group" style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <label className="form-label">Version / Release</label>
              <input className="form-control" value={formModal.version} onChange={(e) => setFormModal((p) => ({ ...p, version: e.target.value }))} placeholder="e.g. 2026.10.1" />
            </div>
            <div style={{ flex: 1 }}>
              <label className="form-label">Change Type</label>
              <select className="form-control" value={formModal.change_type} onChange={(e) => setFormModal((p) => ({ ...p, change_type: e.target.value }))}>
                {CHANGE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">What changed</label>
            <textarea className="form-control" rows={2} value={formModal.description} onChange={(e) => setFormModal((p) => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Why (business/compliance reason)</label>
            <textarea className="form-control" rows={2} value={formModal.reason} onChange={(e) => setFormModal((p) => ({ ...p, reason: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Risk Assessment *</label>
            <textarea className="form-control" rows={2} placeholder="What could go wrong, and how severe?" value={formModal.risk_assessment} onChange={(e) => setFormModal((p) => ({ ...p, risk_assessment: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Impact Assessment *</label>
            <textarea className="form-control" rows={2} placeholder="Which modules/records/users are affected?" value={formModal.impact_assessment} onChange={(e) => setFormModal((p) => ({ ...p, impact_assessment: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Test Evidence *</label>
            <textarea className="form-control" rows={2} placeholder="What was tested, and the result" value={formModal.test_evidence} onChange={(e) => setFormModal((p) => ({ ...p, test_evidence: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Rollback Plan</label>
            <textarea className="form-control" rows={2} value={formModal.rollback_plan} onChange={(e) => setFormModal((p) => ({ ...p, rollback_plan: e.target.value }))} />
          </div>
          <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>Title, Risk Assessment, Impact Assessment and Test Evidence must be filled in before this can be submitted for QA approval.</p>
        </Modal>
      )}

      {/* ── View Modal ───────────────────────────────────────────────────────── */}
      {viewModal && (
        <Modal isOpen onClose={() => setViewModal(null)} title={viewModal.title} size="lg" footer={<button className="btn btn-outline" onClick={() => setViewModal(null)}>Close</button>}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
            <StatusBadge status={viewModal.status} />
            <span className="text-muted">{viewModal.change_type} · {viewModal.version || 'no version tag'}</span>
          </div>
          {[
            ['What changed', viewModal.description],
            ['Why', viewModal.reason],
            ['Risk Assessment', viewModal.risk_assessment],
            ['Impact Assessment', viewModal.impact_assessment],
            ['Test Evidence', viewModal.test_evidence],
            ['Rollback Plan', viewModal.rollback_plan],
          ].map(([label, value]) => (
            <div key={label} className="form-group">
              <label className="form-label">{label}</label>
              <p style={{ whiteSpace: 'pre-wrap' }}>{value || '—'}</p>
            </div>
          ))}
          {viewModal.qa_approved_by_name && (
            <div className="form-group">
              <label className="form-label">QA Decision</label>
              <p>{viewModal.qa_approval_decision} by {viewModal.qa_approved_by_name} on {viewModal.qa_approved_at ? new Date(viewModal.qa_approved_at).toLocaleString() : ''}</p>
              {viewModal.qa_approval_comment && <p className="text-muted">"{viewModal.qa_approval_comment}"</p>}
            </div>
          )}
          {viewModal.released_at && (
            <div className="form-group">
              <label className="form-label">Released</label>
              <p>{new Date(viewModal.released_at).toLocaleString()}</p>
            </div>
          )}
        </Modal>
      )}

      {/* ── Decide Modal ─────────────────────────────────────────────────────── */}
      {decideModal && (
        <Modal
          isOpen
          onClose={() => setDecideModal(null)}
          title={`${decideModal.decision === 'approve' ? 'Approve & Release' : decideModal.decision === 'return' ? 'Return to Draft' : 'Reject'} — Electronic Signature`}
          size="sm"
          footer={
            <>
              <button className="btn btn-outline" onClick={() => setDecideModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={submitDecide} disabled={saving}>{saving ? 'Saving...' : 'Confirm'}</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Comment</label>
            <textarea className="form-control" rows={2} value={decideModal.comment} onChange={(e) => setDecideModal((p) => ({ ...p, comment: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Login Password *</label>
            <input type="password" className="form-control" autoComplete="new-password" value={decideModal.password} onChange={(e) => setDecideModal((p) => ({ ...p, password: e.target.value }))} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ChangeControl;
